import React from 'react';
import type { ComponentType, SVGProps } from 'react';
import type { ToolId } from 'config/toolDefinitionsConfig';
import type {
  Connect2DSelectionState,
  RegulatorConnectSelectionState,
} from '../../rendering/Panels/Renderers/CornerstoneManager';
import { ModifierType } from '../../segmentManager';
import styles from '../SegmentManagerList.module.scss';
import Button from '@components/Button';
import Slider from '@components/Slider';
import {
  getMouseTooltipTriggerProps,
  type MouseTooltipHandlers,
} from '@components/Tooltip/useMouseTooltip';

import type { SegmentManagerIcons } from './segmentManagerIcons';

const WHEEL_STEP = 1;

const clampValue = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const getWheelStep = (deltaY: number) => {
  if (deltaY === 0) return 0;
  return Math.sign(-deltaY) * WHEEL_STEP;
};

const handleWheelStep = (
  event: React.WheelEvent<HTMLElement>,
  value: number,
  min: number,
  max: number,
  onChange: (value: number) => void,
) => {
  const wheelStep = getWheelStep(event.deltaY) * (event.shiftKey ? 10 : 1);
  if (wheelStep === 0) return;

  // event.preventDefault();
  event.stopPropagation();

  const nextValue = clampValue(value + wheelStep, min, max);
  if (nextValue !== value) {
    onChange(nextValue);
  }
};

type Props = {
  showRegulatorConnectTools: boolean;
  showConnect2DTools: boolean;
  enabled: boolean;
  hasVR: boolean;
  isSelected2DPanel: boolean;
  activeToolId?: ToolId;
  modifierType: ModifierType;
  status: RegulatorConnectSelectionState;
  connect2DStatus: Connect2DSelectionState;
  connect2DApplying: boolean;
  brushStep: number;
  thickness: number;
  thicknessText: string;
  huEnabled: boolean;
  huMinText: string;
  huMaxText: string;
  icons: SegmentManagerIcons;
  onToggle: () => void;
  onApply: () => void;
  onClear: () => void;
  onConnect2DToolToggle: () => void;
  onConnect2DApply: () => void;
  onConnect2DClear: () => void;
  onBrushStepChange: (value: number) => void;
  onThicknessChange: (value: number) => void;
  onThicknessTextChange: (value: string) => void;
  onThicknessBlur: () => void;
  onHuEnabledChange: (enabled: boolean) => void;
  onHuRangeTextChange: (
    key: 'regulatorConnectHuMin' | 'regulatorConnectHuMax',
    value: string,
  ) => void;
  onHuRangeBlur: () => void;
  onInputPointerDown: React.PointerEventHandler<HTMLInputElement>;
  onInputMouseDown: React.MouseEventHandler<HTMLInputElement>;
  onInputClick: React.MouseEventHandler<HTMLInputElement>;
  onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onInputWheel: React.WheelEventHandler<HTMLInputElement>;
  tooltipHandlers: MouseTooltipHandlers;
};

export default function RegulatorConnectPanel({
  showRegulatorConnectTools,
  showConnect2DTools,
  enabled,
  hasVR,
  isSelected2DPanel,
  activeToolId,
  modifierType,
  status,
  connect2DStatus,
  connect2DApplying,
  brushStep,
  thickness,
  thicknessText,
  huEnabled,
  huMinText,
  huMaxText,
  icons,
  onToggle,
  onApply,
  onClear,
  onConnect2DToolToggle,
  onConnect2DApply,
  onConnect2DClear,
  onBrushStepChange,
  onThicknessChange,
  onThicknessTextChange,
  onThicknessBlur,
  onHuEnabledChange,
  onHuRangeTextChange,
  onHuRangeBlur,
  onInputPointerDown,
  onInputMouseDown,
  onInputClick,
  onInputKeyDown,
  onInputWheel,
  tooltipHandlers,
}: Props) {
  const { Edit: EditIcon, check: CheckIcon, close: CloseIcon } = icons;

  const connect2DActive =
    modifierType === ModifierType.CONNECT_2D || activeToolId === 'Connect2D';


  const handleBrushStepWheel = (event: React.WheelEvent<HTMLElement>) =>
    handleWheelStep(event, thickness, 1, 20, onThicknessChange);

  const renderIconActionButton = ({
    active,
    disabled,
    label,
    onClick,
    icon,
    type,
    shortcut,
  }: {
    active?: boolean;
    disabled?: boolean;
    label: string;
    onClick: () => void;
    icon: React.ReactElement;
    type: 'mode' | 'apply' | 'clear';
    shortcut?:string,
  }) => (
    <Button
      className={`${styles.iconOnlyToolButton} segment-manager-button-hug-icon20${
        active ? ' active' : ''
      }${type === 'apply' && !disabled ? ` ${styles.applyReady}` : ''
      }`}
      data-action-type={type}
      {...getMouseTooltipTriggerProps(label, tooltipHandlers, shortcut)}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      startIcon={icon}
    />
  );

  const ConnectStateBadges = ({
    start,
    end,
  }: {
    start: unknown;
    end: unknown;
  }) => (
    <div className={styles.pointStateRowCompact}>
      {(['Start', 'End'] as const).map((label) => {
        const active = label === 'Start' ? start : end;

        return (
          <span
            key={label}
            className={`${styles.pointStateBadge} ${
              active ? styles.pointStateBadgeActive : ''
            }`}
          >
            <span className={styles.pointStateDot} />
            {label}
          </span>
        );
      })}
    </div>
  );

  const NumberSlider = ({
    label,
    value,
    sliderValue,
    min,
    max,
    disabled,
    inputMode,
    onInputChange,
    onSliderChange,
    inputProps,
  }: {
    label: string;
    value: number | string;
    sliderValue: number;
    min: number;
    max: number;
    disabled?: boolean;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
    onInputChange: (value: string) => void;
    onSliderChange: (value: number) => void;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  }) => (
    <div className={styles.sizeContainer}>
      <div className={styles.sizeRow}>
        <span className={styles.sizeLabelContainer}>{label}</span>
        <input
          className={styles.sizeValueContainer}
          type="number"
          disabled={disabled}
          value={value}
          min={min}
          max={max}
          //  onWheel={(event) => handleBrushStepWheel(event)}
          inputMode={inputMode}
          onChange={(event) => onInputChange(event.target.value)}
          onWheel={(event) => {
            handleWheelStep(event, thickness, 1, 20, (value) =>
              onThicknessTextChange(String(value)),
            );
          }}
          {...inputProps}
        />
      </div>

      <Slider
        min={min}
        max={max}
        value={sliderValue}
        onChange={onSliderChange}
        onWheel={(event) => {
          handleWheelStep(event, thickness, 1, 20, onSliderChange);
        }}
      />
    </div>
  );
  return (
    <>
      <div className={styles.divider} />

      <div className={styles.toolContainer}>
        Connect Tool
        <div className={styles.frame21}>
          <div className={styles.frame20}>
            <div
              className={`${styles.modiContainer} ${styles.connectToolCompact}`}
            >
              {showRegulatorConnectTools && (
                <div className={styles.connectToolSection}>
                  <div className={styles.connectToolContent}>
                    <div className={styles.connectToolInfo}>
                      <span className={styles.modiActionsLabel}>
                        Connect
                        <span>
                          (<span className={styles.shortcutKey}>C</span>)
                        </span>
                      </span>

                      <ConnectStateBadges
                        start={status.start}
                        end={status.end}
                      />
                    </div>

                    <div className={styles.connectToolButtonGroup}>
                      {renderIconActionButton({
                        label: 'Point Connect selection mode',
                        active: enabled,
                        disabled:
                          hasVR || connect2DApplying || !isSelected2DPanel,
                        onClick: onToggle,
                        icon: <EditIcon />,
                        shortcut:'C',
                        type: 'mode',
                      })}

                      {renderIconActionButton({
                        label: 'Apply',
                        disabled: !status.ready || connect2DApplying,
                        onClick: onApply,
                        icon: <CheckIcon />,
                        type: 'apply',
                        shortcut:'Enter',
                      })}

                      {renderIconActionButton({
                        label: 'Clear Point Connect',
                        disabled:
                          (!status.start && !status.end) || connect2DApplying,
                        onClick: onClear,
                        icon: <CloseIcon />,
                        type: 'clear',
                        shortcut:'ESC',
                      })}
                    </div>
                  </div>
                </div>
              )}

              {enabled && showRegulatorConnectTools && (
                <div className={styles.connectToolOptionsCompact}>
                  <NumberSlider
                    label="Thickness"
                    value={thicknessText}
                    sliderValue={thickness}
                    min={1}
                    max={20}
                    inputMode="numeric"
                    onInputChange={onThicknessTextChange}
                    onSliderChange={onThicknessChange}
                    inputProps={{
                      onWheel: handleBrushStepWheel,
                    }}
                  />

                  {/* <div className={styles.HuContainerCompact}>
                    <label className={styles.huCheckRow}>
                      <input
                        type="checkbox"
                        checked={huEnabled}
                        onChange={(event) =>
                          onHuEnabledChange(event.target.checked)
                        }
                      />
                      <span>HU Filter</span>
                    </label>

                    {huEnabled && (
                      <div className={styles.HuRow}>
                        <span className={styles.HuLabelContainer}>Range</span>

                        <div className={styles.HuValueContainer}>
                          <input
                            className={styles.HuMinValueContainer}
                            type="number"
                            value={huMinText}
                            inputMode="numeric"
                            onPointerDown={onInputPointerDown}
                            onMouseDown={onInputMouseDown}
                            onClick={onInputClick}
                            onKeyDown={onInputKeyDown}
                            onWheel={onInputWheel}
                            onBlur={onHuRangeBlur}
                            onChange={(event) =>
                              onHuRangeTextChange(
                                'regulatorConnectHuMin',
                                event.target.value,
                              )
                            }
                          />

                          <span className={styles.HuSeparatorContainer}>~</span>

                          <input
                            className={styles.HuMaxValueContainer}
                            type="number"
                            value={huMaxText}
                            inputMode="numeric"
                            onPointerDown={onInputPointerDown}
                            onMouseDown={onInputMouseDown}
                            onClick={onInputClick}
                            onKeyDown={onInputKeyDown}
                            onWheel={onInputWheel}
                            onBlur={onHuRangeBlur}
                            onChange={(event) =>
                              onHuRangeTextChange(
                                'regulatorConnectHuMax',
                                event.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div> */}
                </div>
              )}

              {/* {showConnect2DTools && (
                <div className={styles.connectToolSection}>
                  <div className={styles.connectToolContent}>
                    <div className={styles.connectToolInfo}>
                      <span className={styles.modiActionsLabel}>
                        2D Region Connect [F]
                      </span>

                      <ConnectStateBadges
                        start={connect2DStatus.start}
                        end={connect2DStatus.end}
                      />
                    </div>

                    <div className={styles.connectToolButtonGroup}>
                      {renderIconActionButton({
                        label: 'Connect 2D selection mode [F]',
                        active: connect2DActive,
                        disabled:
                          hasVR || !isSelected2DPanel || connect2DApplying
                        ,
                        onClick: onConnect2DToolToggle,
                        icon: <EditIcon />,
                        type: 'mode',
                      })}

                      {renderIconActionButton({
                        label: 'Apply Connect 2D',
                        disabled: !connect2DStatus.ready || connect2DApplying,
                        onClick: onConnect2DApply,
                        icon: <CheckIcon />,
                        type: 'apply',
                      })}

                      {renderIconActionButton({
                        label: 'Clear Connect 2D [Esc]',
                        disabled:
                          (!connect2DStatus.start && !connect2DStatus.end) ||
                          connect2DApplying
                        ,
                        onClick: onConnect2DClear,
                        icon: <CloseIcon />,
                        type: 'clear',
                      })}
                    </div>
                  </div>
                </div>
              )} */}

              {showConnect2DTools && connect2DActive && isSelected2DPanel && (
                <div className={styles.connectToolOptionsCompact}>
                  <NumberSlider
                    label="Size"
                    value={brushStep}
                    sliderValue={brushStep}
                    min={1}
                    max={10}
                    disabled={connect2DApplying}
                    onInputChange={(value) => onBrushStepChange(Number(value))}
                    onSliderChange={onBrushStepChange}
                  />

                  {(connect2DApplying || connect2DStatus.message) && (
                    <div className={styles.connectStatusMessage}>
                      {connect2DApplying
                        ? 'Applying Connect 2D...'
                        : connect2DStatus.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
